/**
 * cas-9b1f: the UI language is an account preference, not just a browser setting.
 *
 * - Switching language while signed in saves accounts.locale (PUT /users/me
 *   { accountLocale }). Recipient emails read the same value.
 * - On sign-in in any browser, the saved preference wins over that browser's
 *   localStorage, so a second device opens in the account's language.
 * - Signed out, the browser-local choice (localStorage 'locale') is all there is. A pick
 *   made while signed out (say on /sign-in) is marked pending and, on the next sign-in,
 *   wins over the account's older value and is saved to it.
 * - Restoring never writes back; only an explicit switch saves.
 *
 * Kept separate from the SMS consent locale (UpdateUserDto.locale), which only
 * records the language of a consent.
 */

export type UiLocale = 'en' | 'es';
export type AccountLocale = 'en-US' | 'es';

/** localStorage key marking a language picked while signed out and not yet saved. */
export const LOCALE_CHOICE_PENDING_KEY = 'locale_choice_pending';

/** The value stored on the account for a UI language. */
export function accountLocaleFromUi(code: UiLocale): AccountLocale {
  return code === 'es' ? 'es' : 'en-US';
}

/** The UI language for a stored account value; null when it is missing or unsupported. */
export function uiLocaleFromAccount(stored: string | null | undefined): UiLocale | null {
  const language = stored?.trim().toLowerCase().replace(/_/g, '-').split('-')[0];
  if (language === 'es') return 'es';
  if (language === 'en') return 'en';
  return null;
}

/** The UI language to switch to after the account loads, or null to leave it alone. */
export function restoreDecision(input: {
  authenticated: boolean;
  accountLocale: string | null | undefined;
  current: string | null | undefined;
}): UiLocale | null {
  if (!input.authenticated) return null;
  const wanted = uiLocaleFromAccount(input.accountLocale);
  return wanted && wanted !== input.current ? wanted : null;
}

type ApiRequest = (
  url: string,
  init: { method: 'PUT'; headers: Record<string, string>; body: string }
) => Promise<unknown>;

/**
 * Save an explicit language switch to the signed-in account. Best-effort: the local switch
 * has already happened, and a failed save leaves it in place (returns false).
 */
export async function persistUiLocale(
  code: UiLocale,
  deps: {
    authenticated: boolean;
    apiRequest: ApiRequest;
    setAccountLocale: (value: AccountLocale) => void;
  }
): Promise<boolean> {
  if (!deps.authenticated) return false;
  const accountLocale = accountLocaleFromUi(code);
  try {
    await deps.apiRequest('/api/v1/users/me', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountLocale })
    });
    deps.setAccountLocale(accountLocale);
    return true;
  } catch {
    return false;
  }
}

/**
 * What to do when a signed-in account loads. A pending signed-out pick is the person's latest
 * explicit choice: keep it (apply) and save it (persist) when it differs from the account.
 * Otherwise the account preference wins.
 */
export function reconcileOnSignIn(input: {
  accountLocale: string | null | undefined;
  pendingChoice: string | null | undefined;
  current: string | null | undefined;
  /**
   * cas-fd86: the non-default locale the page URL carries (`/es/...` → 'es'), else null.
   * A prefixed URL is an explicit choice for this page: the account language does not
   * override it (accounts.locale defaults to en-US, so it is not proof of a preference),
   * and it is not saved. The unprefixed default URL cannot say "English on purpose", so
   * there the account still wins.
   */
  urlLocale?: string | null;
}): { apply: UiLocale | null; persist: UiLocale | null } {
  const pending = uiLocaleFromAccount(input.pendingChoice);
  if (pending) {
    return {
      apply: pending !== input.current ? pending : null,
      persist: uiLocaleFromAccount(input.accountLocale) !== pending ? pending : null
    };
  }
  if (uiLocaleFromAccount(input.urlLocale)) return { apply: null, persist: null };
  return {
    apply: restoreDecision({
      authenticated: true,
      accountLocale: input.accountLocale,
      current: input.current
    }),
    persist: null
  };
}
